/**
 * Domain Layer - MedicalReservation Entity
 * 診療予約エンティティ定義
 */

/**
 * 診療予約エンティティ（CSVから読み込んだ生データ）
 */
export interface MedicalReservation {
  /** 行ID（CSV内の行番号） */
  readonly id: number;
  /** AnyBot ID */
  readonly anybotId: string;
  /** 診療開始時間（YYYY-MM-DD HH:mm形式） */
  readonly consultationDateTime: string;
  /** 患者名 */
  readonly patientName: string;
  /** 担当医師名 */
  readonly doctorName: string;
  /** ステータス（日本語ラベル） */
  readonly status: string;
  /** その他の元データ（動的なフィールド） */
  readonly rawData: Readonly<Record<string, string>>;
}

/**
 * 加工済み診療予約データ
 */
export interface ProcessedReservation {
  /** 元の行ID */
  readonly id: number;
  /** AnyBot ID */
  readonly anybotId: string;
  /** 診療開始時間（YYYY-MM-DD HH:mm形式） */
  readonly consultationDateTime: string;
  /** 患者名 */
  readonly patientName: string;
  /** 担当医師名（加工後） */
  readonly doctorName: string;
  /** ステータス（加工後、日本語ラベル） */
  readonly status: string;
  /** 元のステータス */
  readonly originalStatus: string;
  /** 元の医師名 */
  readonly originalDoctorName: string;
  /** ステータスが変換されたか */
  readonly isStatusConverted: boolean;
  /** 医師名が変更されたか */
  readonly isDoctorChanged: boolean;
  /** その他の元データ（動的なフィールド） */
  readonly rawData: Readonly<Record<string, string>>;
}

/**
 * CSVの列名定数
 */
export const CSV_COLUMN_NAMES = {
  ANYBOT_ID: 'anybot ID',
  CONSULTATION_DATE_TIME: '診療開始時間',
  PATIENT_NAME: '患者名',
  DOCTOR_NAME: '担当医師',
  STATUS: 'ステータス',
} as const;

/**
 * 生データからMedicalReservationエンティティを生成する
 * @param rowIndex 行インデックス
 * @param rawData 行データ（ヘッダーをキーとしたオブジェクト）
 * @returns MedicalReservationエンティティ
 */
export const createMedicalReservation = (
  rowIndex: number,
  rawData: Record<string, string>
): MedicalReservation => ({
  id: rowIndex,
  anybotId: rawData[CSV_COLUMN_NAMES.ANYBOT_ID] ?? '',
  consultationDateTime: rawData[CSV_COLUMN_NAMES.CONSULTATION_DATE_TIME] ?? '',
  patientName: rawData[CSV_COLUMN_NAMES.PATIENT_NAME] ?? '',
  doctorName: rawData[CSV_COLUMN_NAMES.DOCTOR_NAME] ?? '',
  status: rawData[CSV_COLUMN_NAMES.STATUS] ?? '',
  rawData: Object.freeze({ ...rawData }),
});

/**
 * MedicalReservationからProcessedReservationを生成する
 * @param reservation 元の予約データ
 * @param processedStatus 加工後のステータス
 * @param processedDoctorName 加工後の医師名
 * @returns ProcessedReservation
 */
export const createProcessedReservation = (
  reservation: MedicalReservation,
  processedStatus: string,
  processedDoctorName: string
): ProcessedReservation => ({
  id: reservation.id,
  anybotId: reservation.anybotId,
  consultationDateTime: reservation.consultationDateTime,
  patientName: reservation.patientName,
  doctorName: processedDoctorName,
  status: processedStatus,
  originalStatus: reservation.status,
  originalDoctorName: reservation.doctorName,
  isStatusConverted: reservation.status !== processedStatus,
  isDoctorChanged: reservation.doctorName !== processedDoctorName,
  rawData: reservation.rawData,
});

/**
 * ProcessedReservationを更新する（イミュータブル）
 * @param reservation 元の加工済み予約
 * @param updates 更新内容
 * @returns 新しいProcessedReservation
 */
export const updateProcessedReservation = (
  reservation: ProcessedReservation,
  updates: Partial<Pick<ProcessedReservation, 'status' | 'doctorName'>>
): ProcessedReservation => ({
  ...reservation,
  status: updates.status ?? reservation.status,
  doctorName: updates.doctorName ?? reservation.doctorName,
  isStatusConverted:
    updates.status !== undefined
      ? reservation.originalStatus !== updates.status
      : reservation.isStatusConverted,
  isDoctorChanged:
    updates.doctorName !== undefined
      ? reservation.originalDoctorName !== updates.doctorName
      : reservation.isDoctorChanged,
});

/**
 * 診療開始時間をパースしてDateオブジェクトに変換する
 * @param consultationDateTime 診療開始時間文字列
 * @returns Dateオブジェクト（パース失敗時はnull）
 */
export const parseConsultationDateTime = (
  consultationDateTime: string
): Date | null => {
  if (!consultationDateTime) {
    return null;
  }

  // 様々な形式に対応
  // "YYYY-MM-DD HH:mm" or "YYYY/MM/DD HH:mm" or "YYYY-MM-DDTHH:mm"
  const normalized = consultationDateTime
    .replace(/\//g, '-')
    .replace(' ', 'T');

  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : date;
};
